'use client';

import { type Ref, useMemo } from 'react';
import { sanitizeCSSVariables } from '../../../util/style';
import { buildTypographyStyleValue, Typography, type TypographyProps } from '../../typography';
import { useMergedTypography } from '../../typography/context';
import { BaseLink, type BaseLinkProps } from '../base-link';
import { buildTextLinkClassName } from './utils';

export type TextLinkSize = 'xsmall' | 'small' | 'medium' | 'large';

export const baseTextLinkTypographyBySize: Record<TextLinkSize, TypographyProps> = {
  xsmall: {
    size: '0.75rem',
    lineHeight: '1rem',
    weight: 'medium',
    tone: 'md',
  },
  small: {
    size: '0.875rem',
    lineHeight: '1.5rem',
    weight: 'medium',
    tone: 'md',
  },
  medium: {
    size: '1rem',
    lineHeight: '1.5rem',
    weight: 'medium',
    tone: 'md',
  },
  large: {
    size: '1.125rem',
    lineHeight: '1.625rem',
    weight: 'medium',
    tone: 'md',
  },
};

export interface TextLinkProps extends BaseLinkProps {
  fullWidth?: boolean;
  ref?: Ref<HTMLAnchorElement>;
  size?: TextLinkSize;
  typography?: TypographyProps;
}

export function TextLink({
  className,
  children,
  fullWidth,
  size = 'small',
  style: providedStyle,
  typography: providedTypography,
  underline = 'always',
  ref,
  ...rest
}: TextLinkProps) {
  const typography = useMergedTypography({ ...baseTextLinkTypographyBySize[size], ...providedTypography } as TypographyProps, 'current');
  const style = useMemo(
    () => sanitizeCSSVariables(buildTypographyStyleValue(typography.tone, providedStyle, '--ixf-text-link-color')),
    [typography, providedStyle],
  );

  return (
    <BaseLink className={buildTextLinkClassName({ className, fullWidth, underline })} ref={ref} style={style} {...rest}>
      <Typography {...typography}>{children}</Typography>
    </BaseLink>
  );
}
